import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { api } from '../hooks/api.js';
import type { ComponentUsageDoc } from '@blogforge/shared';

interface Props {
  componentId: string;
  componentName: string;
  onClose: () => void;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'applying' }
  | { kind: 'success'; applied: number; skipped: number; failed: number }
  | { kind: 'error'; message: string };

/**
 * "Push update" — applies the component's current content onto selected
 * documents. Documents whose instance was left untouched are pre-checked;
 * customized documents are listed (unchecked by default) so the author
 * decides whether to overwrite their local edits or leave them alone.
 * Sent emails and archived docs are not updatable and are shown disabled.
 */
export function ApplyUpdateModal({ componentId, componentName, onClose }: Props) {
  const qc = useQueryClient();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const { data, isLoading } = useQuery({
    queryKey: ['component-usages', componentId],
    queryFn: () => api.components.usages(componentId),
  });

  const usages = (data as { data?: ComponentUsageDoc[] } | undefined)?.data ?? [];

  // Once usages arrive, pre-check everything updatable and untouched.
  const [initialised, setInitialised] = useState(false);
  if (!initialised && !isLoading) {
    setChecked(
      new Set(usages.filter((u) => u.updatable && !u.customized).map((u) => u.instanceId)),
    );
    setInitialised(true);
  }

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleApply = async () => {
    const updates = usages
      .filter((u) => checked.has(u.instanceId))
      .map((u) => ({
        kind: u.kind,
        documentId: u.documentId,
        instanceId: u.instanceId,
        replace: true,
      }));
    if (updates.length === 0) return;
    setStatus({ kind: 'applying' });
    try {
      const res = (await api.components.apply(componentId, { updates })) as {
        applied: unknown[];
        skipped: unknown[];
        failures: unknown[];
      };
      setStatus({
        kind: 'success',
        applied: res.applied.length,
        skipped: res.skipped.length,
        failed: res.failures.length,
      });
      void qc.invalidateQueries({ queryKey: ['components'] });
      void qc.invalidateQueries({ queryKey: ['component-usages', componentId] });
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const label = (u: ComponentUsageDoc) => {
    const badges: string[] = [u.kind === 'email' ? 'Email' : 'Post'];
    badges.push(u.status);
    if (u.customized) badges.push('edited since insert');
    if (u.stale && !u.customized) badges.push('outdated content');
    return badges.join(' · ');
  };

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (status.kind !== 'applying') onClose();
      }}
    >
      <div
        className="modal-card w-full max-w-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-[17px] font-bold tracking-tight text-ink">Push “{componentName}” update</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={status.kind === 'applying'}
            className="rounded p-1 text-muted hover:bg-card-2 hover:text-ink-soft disabled:opacity-40"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p className="mb-4 text-xs text-muted">
          Replace the component's blocks in the documents below. Published posts and unsent emails
          are rebuilt automatically. Sent emails can't be changed.
        </p>

        {status.kind === 'success' ? (
          <div className="rounded-lg bg-ok/10 px-4 py-6 text-center">
            <p className="text-sm font-medium text-ok">Update pushed 🎉</p>
            <p className="mt-1 text-xs text-ok">
              {status.applied} document{status.applied === 1 ? '' : 's'} updated
              {status.skipped > 0 ? ` · ${status.skipped} skipped` : ''}
              {status.failed > 0 ? ` · ${status.failed} failed` : ''}
            </p>
            <button type="button" onClick={onClose} className="btn-primary mt-4 px-4 py-2 text-xs">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-line p-2">
              {isLoading ? (
                <p className="px-2 py-4 text-center text-xs text-muted">Loading usages…</p>
              ) : usages.length === 0 ? (
                <p className="px-2 py-4 text-center text-xs text-muted">
                  This component isn't used anywhere yet.
                </p>
              ) : (
                usages.map((u) => {
                  const disabled = !u.updatable;
                  return (
                    <label
                      key={u.instanceId}
                      className={`flex items-start gap-2 rounded-md px-2 py-2 text-sm ${
                        disabled
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer hover:bg-bg-soft'
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={checked.has(u.instanceId)}
                        onChange={() => toggle(u.instanceId)}
                        className="text-accent focus:ring-accent mt-0.5 h-3.5 w-3.5 rounded border-line-strong"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-medium text-ink">
                          {u.title}
                        </span>
                        <span className="block text-[10px] text-muted">{label(u)}</span>
                      </span>
                      {u.customized && (
                        <span className="flex-none rounded bg-warn/10 px-1.5 py-0.5 text-[10px] font-medium text-warn">
                          Overrides local edits
                        </span>
                      )}
                      {!u.updatable && (
                        <span className="flex-none rounded bg-card-2 px-1.5 py-0.5 text-[10px] font-medium text-muted">
                          Not updatable
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>

            {status.kind === 'error' && (
              <p className="mt-2 text-xs text-danger">{status.message}</p>
            )}

            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-muted">
                {checked.size} of {usages.length} selected
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={status.kind === 'applying'}
                  className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={status.kind === 'applying' || checked.size === 0}
                  className="btn-primary px-3 py-1.5 text-xs disabled:opacity-40"
                >
                  <Send size={13} />
                  {status.kind === 'applying' ? 'Applying…' : 'Push update'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
